import { DraftOrderResponse } from 'models/payment.model';
import { Order, ProductFormInputs } from 'models/purchaseForm.model';
import { useReducer } from 'react';

export enum CheckoutState {
  NoSelection,
  AddRecipientDetails,
  Login,
  Register,
  GuestCheckout,
  AlreadyLoggedIn,
  FinalisePayment,
  Submitting,
}

export enum PaypalStatus {
  Cancelled,
  Processing,
  Idle,
}

export enum PaymentOptions {
  Paypal,
  CreditCard,
  ApplePay,
  GooglePay,
}

export enum PaymentTypes {
  NotSelected,
  Loading,
  Paypal,
  CreditCard,
  ApplePay,
  GooglePay,
}

export enum PurchaseType {
  Self,
  Friend,
  TypeNotSelected,
}

export enum PurchaseKind {
  User,
  Guest,
  KindNotSelected,
}

export enum TabDefaultIndex {
  GiftCard,
  Giphy,
  Video,
}

export type state = {
  collectBillingAddress: boolean;
  error?: string;
  checkoutState: CheckoutState;
  paymentOptions: PaymentOptions[];
  paypal: PaypalStatus;
  step: number;
  furthestStep: number;
  previousStep?: string;
  formValues: ProductFormInputs;
  orderResult: Order;
  tabDefaultIndex: TabDefaultIndex;
  defaultGiftCardPage: number;
  defaultGiphyPage: number;
  manualCheckoutType: boolean;
  previewButtonClicked: boolean;
  recipientToEdit?: number;
  draftOrder?: DraftOrderResponse & { type: 'user' | 'guest' };
};

const initialData: ProductFormInputs = {
  deliveryOption: 'Instant',
  giftMedia: null,
  giftTheme: null,
  guestEmail: null,
  guestFirstName: null,
  guestLastName: null,
  message: null,
  giphy: null,
  nzTnC: false,
  paymentMethodId: 'creditCard',
  prezzeeEDM: false,
  prezzeeTnC: false,
  promo: null,
  purchaseType: null,
  senderAlias: null,
  value: '',
  recipients: [
    {
      name: '',
      email: null,
      phone: null,
      smsCheckbox: false,
      emailCheckbox: false,
    },
  ],
};

export const initialState: state = {
  checkoutState: CheckoutState.NoSelection,
  collectBillingAddress: false,
  error: null,
  paymentOptions: [PaymentOptions.CreditCard],
  paypal: PaypalStatus.Idle,
  step: 0,
  furthestStep: 0,
  previousStep: null,
  formValues: initialData,
  orderResult: null,
  tabDefaultIndex: TabDefaultIndex.GiftCard,
  defaultGiftCardPage: 0,
  defaultGiphyPage: 0,
  manualCheckoutType: false,
  previewButtonClicked: false,
};

export enum ActionType {
  PaypalCancelled,
  PaypalIdle,
  RemoveError,
  Reset,
  SetCheckoutState,
  SetError,
  SetHasCreditCard,
  SetHasPaypal,
  SetHasApplePay,
  SetHasGooglePay,
  SetPaymentNotSelected,
  SetPaypalNonce,
  SetOrderResult,
  SetStep,
  SetPreviousStep,
  SetFormValues,
  SetTabDefaultIndex,
  SetDefaultGiftCardPage,
  SetDefaultGiphyPage,
  SetManualCheckout,
  SetPreviewButtonClicked,
  SetRecipientToEdit,
  SetDraftOrder,
}

export interface IAction {
  type: ActionType;
  payload?: any;
  paymentType?: PaymentTypes;
}

export const reducer = (state = initialState, action: IAction): state => {
  const { type, payload } = action;

  switch (type) {
    case ActionType.PaypalCancelled:
      return {
        ...state,
        paypal: PaypalStatus.Cancelled,
      };
    case ActionType.PaypalIdle:
      return {
        ...state,
        paypal: PaypalStatus.Idle,
      };
    case ActionType.SetOrderResult:
      return { ...state, orderResult: payload };
    case ActionType.RemoveError:
      return { ...state, error: null };
    case ActionType.Reset:
      return initialState;
    case ActionType.SetCheckoutState:
      return { ...state, checkoutState: payload };
    case ActionType.SetError:
      return { ...state, error: payload };
    case ActionType.SetHasCreditCard:
      return { ...state, paymentOptions: state.paymentOptions.concat(PaymentOptions.CreditCard) };
    case ActionType.SetHasPaypal:
      return { ...state, paymentOptions: state.paymentOptions.concat(PaymentOptions.Paypal) };
    case ActionType.SetHasApplePay:
      return { ...state, paymentOptions: state.paymentOptions.concat(PaymentOptions.ApplePay) };
    case ActionType.SetHasGooglePay:
      return { ...state, paymentOptions: state.paymentOptions.concat(PaymentOptions.GooglePay) };
    case ActionType.SetStep:
      return {
        ...state,
        step: payload,
        furthestStep: payload > state.furthestStep ? payload : state.furthestStep,
      };
    case ActionType.SetPreviousStep:
      return { ...state, previousStep: payload };
    case ActionType.SetFormValues:
      return { ...state, formValues: { ...state.formValues, ...payload } };
    case ActionType.SetTabDefaultIndex:
      return { ...state, tabDefaultIndex: payload };
    case ActionType.SetDefaultGiftCardPage:
      return { ...state, defaultGiftCardPage: payload };
    case ActionType.SetDefaultGiphyPage:
      return { ...state, defaultGiphyPage: payload };
    case ActionType.SetManualCheckout:
      return { ...state, manualCheckoutType: payload };
    case ActionType.SetPreviewButtonClicked:
      return { ...state, previewButtonClicked: true };
    case ActionType.SetRecipientToEdit:
      return { ...state, recipientToEdit: payload };
    case ActionType.SetDraftOrder:
      return { ...state, draftOrder: payload };
    default:
      return state;
  }
};

export const useProductPageReducer = (collectBillingAddress: boolean) => {
  return useReducer(reducer, {
    ...initialState,
    collectBillingAddress,
  });
};
